import Link, { LinkProps } from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import { Link as RouterLink, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRouteMatch } from '../../utils/routeHelper';
import { useGetPreAdmissionDetailQuery } from '@/api/pre-admission/preAdmission';
import { useGetAdmissionRequestDetailQuery } from '@/api/member-management/admissionRequest';
import { useGetInterventionDetailQuery } from '@/api/member-management/intervention';
import { useGetFamilyDetailQuery } from '@/api/family-management/familyApi';
import {
	useGetFieldOptionCommonDetailQuery,
	useGetFieldOptionRegionDetailQuery,
} from '@/api/field-option/fieldOption';

interface LinkRouterProps extends LinkProps {
	to: string;
	replace?: boolean;
}

function LinkRouter(props: LinkRouterProps) {
	return <Link {...props} component={RouterLink} />;
}

export default function RouterBreadcrumbs() {
	const { t } = useTranslation();
	const location = useLocation();
	const pathnames = location.pathname.split('/').filter((x) => x);
	let lastPath = '';
	const {
		preAdmissionId,
		admissionRequestId,
		interventionId,
		myCaseId,
		familyId,
		districtId,
		subDistrictId,
		ecvId,
		buildId,
		fieldType,
		agencyType,
		referralType,
	} = useParams();

	const { data: dictDetails } = useGetFieldOptionCommonDetailQuery(
		fieldType || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !fieldType,
		}
	);
	const { data: agencyDetails } = useGetFieldOptionCommonDetailQuery(
		agencyType || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !agencyType,
		}
	);
	const { data: referralDetails } = useGetFieldOptionCommonDetailQuery(
		referralType || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !referralType,
		}
	);
	const { data: preAdmissionDetails }: any = useGetPreAdmissionDetailQuery(
		preAdmissionId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !preAdmissionId,
		}
	);
	const { data: admissionRequestDetails } = useGetAdmissionRequestDetailQuery(
		admissionRequestId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !admissionRequestId,
		}
	);

	const { data: interventionDetails }: any = useGetInterventionDetailQuery(
		interventionId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !interventionId,
		}
	);
	const { data: newInterventionAdmissionDetails }: any =
		useGetAdmissionRequestDetailQuery(myCaseId || '', {
			refetchOnMountOrArgChange: true,
			skip: !(myCaseId && pathnames.indexOf('create') > -1),
		});
	const { data: familyDetail }: any = useGetFamilyDetailQuery(familyId || '', {
		refetchOnMountOrArgChange: true,
		skip: !familyId,
	});

	const { data: districtDetails } = useGetFieldOptionRegionDetailQuery(
		districtId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !districtId,
		}
	);
	const { data: subDistrictDetails } = useGetFieldOptionRegionDetailQuery(
		subDistrictId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !subDistrictId,
		}
	);
	const { data: evcDistrictDetails } = useGetFieldOptionRegionDetailQuery(
		ecvId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !ecvId,
		}
	);
	const { data: buildDetails } = useGetFieldOptionRegionDetailQuery(
		buildId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !buildId,
		}
	);

	const newInterventionLabel = `${t('drawerListItems.newIntervention')}${
		newInterventionAdmissionDetails?.data.fileNo
			? ` (${newInterventionAdmissionDetails.data.fileNo})`
			: ''
	}`;

	// You need to provide the routes in descendant order.
	// This means that if you have nested routes like:
	// users, users/new, users/edit.
	// Then the order should be ['users/add', 'users/edit', 'users'].
	const breadcrumbNameMap: { [key: string]: string } = {
		'/pre-admission': t('drawerListItems.preAdmissionManagement'),
		'/pre-admission/pre-admission-list': t('drawerListItems.preAdmissionList'),
		'/pre-admission/pre-admission-list/create': t(
			'drawerListItems.newPreAdmission'
		),
		'/pre-admission/pre-admission-list/detail/:preAdmissionId':
			`${t('drawerListItems.preAdmissionDetails')} (${
				preAdmissionDetails?.data.preAdmissionNo
			})` || t('drawerListItems.preAdmissionDetails'),
		'/pre-admission/pre-admission-list/detail/:preAdmissionId/edit-pre-admission':
			t('drawerListItems.editPreAdmission'),
		'/pre-admission/pre-admission-list/detail/:preAdmissionId/edit-client-information':
			t('drawerListItems.editPreAdmission'),
		'/pre-admission/pre-admission-list/detail/:preAdmissionId/edit-referrer-information':
			t('drawerListItems.editPreAdmission'),
		'/pre-admission/pre-admission-list/detail/:preAdmissionId/edit-case-status':
			t('drawerListItems.editPreAdmission'),
		'/pre-admission/pre-admission-list/detail/:preAdmissionId/edit-results': t(
			'drawerListItems.editPreAdmission'
		),
		'/pre-admission/pre-admission-list/detail/:preAdmissionId/forms-management':
			t('drawerListItems.formsManagement'),
		'/pre-admission/pre-admission-list/detail/:preAdmissionId/forms-management/create-enquiry-record':
			t('drawerListItems.newEnquiryRecord'),
		'/pre-admission/pre-admission-list/detail/:preAdmissionId/forms-management/edit-enquiry-record':
			t('drawerListItems.newEnquiryRecord'),
		'/pre-admission/pre-admission-list/detail/:preAdmissionId/forms-management/detail-enquiry-record':
			t('drawerListItems.enquiryRecordDetails'),
		'/pre-admission/pre-admission-list/detail/:preAdmissionId/forms-management/create-face-sheet':
			t('drawerListItems.newFaceSheet'),
		'/pre-admission/pre-admission-list/detail/:preAdmissionId/forms-management/edit-face-sheet':
			t('drawerListItems.newFaceSheet'),
		'/pre-admission/pre-admission-list/detail/:preAdmissionId/forms-management/detail-face-sheet':
			t('drawerListItems.faceSheetDetails'),

		'/pre-admission/pre-admission-list/detail/:preAdmissionId/forms-management/create-appendix4':
			t('drawerListItems.newAppendix4'),
		'/pre-admission/pre-admission-list/detail/:preAdmissionId/forms-management/edit-appendix4':
			t('drawerListItems.newAppendix4'),
		'/pre-admission/pre-admission-list/detail/:preAdmissionId/forms-management/detail-appendix4':
			t('drawerListItems.appendix4Detail'),
		'/pre-admission/pre-admission-list/detail/:preAdmissionId/forms-management/create-appendix5':
			t('drawerListItems.newAppendix5'),
		'/pre-admission/pre-admission-list/detail/:preAdmissionId/forms-management/edit-appendix5':
			t('drawerListItems.newAppendix5'),
		'/pre-admission/pre-admission-list/detail/:preAdmissionId/forms-management/detail-appendix5':
			t('drawerListItems.appendix5Detail'),
		'/pre-admission/pre-admission-list/detail/:preAdmissionId/forms-management/create-appendix6':
			t('drawerListItems.newAppendix6'),
		'/pre-admission/pre-admission-list/detail/:preAdmissionId/forms-management/edit-appendix6':
			t('drawerListItems.newAppendix6'),
		'/pre-admission/pre-admission-list/detail/:preAdmissionId/forms-management/detail-appendix6':
			t('drawerListItems.appendix6Detail'),

		'/pre-admission/pre-admission-list/detail/:preAdmissionId/forms-management/create-appendix7':
			t('drawerListItems.newAppendix7'),
		'/pre-admission/pre-admission-list/detail/:preAdmissionId/forms-management/edit-appendix7':
			t('drawerListItems.newAppendix7'),
		'/pre-admission/pre-admission-list/detail/:preAdmissionId/forms-management/detail-appendix7':
			t('drawerListItems.appendix7Detail'),

		'/pre-admission/pre-admission-list/detail/:preAdmissionId/forms-management/create-member-application':
			t('drawerListItems.newMemberApplication'),
		'/pre-admission/pre-admission-list/detail/:preAdmissionId/forms-management/edit-member-application':
			t('drawerListItems.newMemberApplication'),
		'/pre-admission/pre-admission-list/detail/:preAdmissionId/forms-management/detail-member-application':
			t('drawerListItems.memberApplicationDetail'),

		'/pre-admission/pre-admission-list/detail/:preAdmissionId/forms-management/create-new-consent':
			t('drawerListItems.newNewConsent'),
		'/pre-admission/pre-admission-list/detail/:preAdmissionId/forms-management/edit-new-consent':
			t('drawerListItems.newNewConsent'),
		'/pre-admission/pre-admission-list/detail/:preAdmissionId/forms-management/detail-new-consent':
			t('drawerListItems.newConsentDetail'),

		'/pre-admission/pre-admission-list/detail/:preAdmissionId/forms-management/create-stop-service':
			t('drawerListItems.newStopService'),
		'/pre-admission/pre-admission-list/detail/:preAdmissionId/forms-management/edit-stop-service':
			t('drawerListItems.newStopService'),
		'/pre-admission/pre-admission-list/detail/:preAdmissionId/forms-management/detail-stop-service':
			t('drawerListItems.stopServiceDetail'),

		'/pre-admission/pre-admission-list/detail/:preAdmissionId/forms-management/create-well-being-scale':
			t('drawerListItems.newWellBeingScale'),
		'/pre-admission/pre-admission-list/detail/:preAdmissionId/forms-management/edit-well-being-scale':
			t('drawerListItems.newWellBeingScale'),
		'/pre-admission/pre-admission-list/detail/:preAdmissionId/forms-management/detail-well-being-scale':
			t('drawerListItems.wellBeingScaleDetail'),

		'/pre-admission/pre-admission-list/detail/:preAdmissionId/forms-management/create-recovery-assessment-scale':
			t('drawerListItems.newRecoveryAssessmentScale'),
		'/pre-admission/pre-admission-list/detail/:preAdmissionId/forms-management/edit-recovery-assessment-scale':
			t('drawerListItems.newRecoveryAssessmentScale'),
		'/pre-admission/pre-admission-list/detail/:preAdmissionId/forms-management/detail-recovery-assessment-scale':
			t('drawerListItems.recoveryAssessmentScaleDetail'),

		'/pre-admission/pre-admission-list/detail/:preAdmissionId/forms-management/create-dsm5-adult':
			t('drawerListItems.newDsm5Adult'),
		'/pre-admission/pre-admission-list/detail/:preAdmissionId/forms-management/edit-dsm5-adult':
			t('drawerListItems.newDsm5Adult'),
		'/pre-admission/pre-admission-list/detail/:preAdmissionId/forms-management/detail-dsm5-adult':
			t('drawerListItems.dsm5AdultDetail'),

		'/pre-admission/pre-admission-list/detail/:preAdmissionId/forms-management/create-dsm5-teen':
			t('drawerListItems.newDsm5Teen'),
		'/pre-admission/pre-admission-list/detail/:preAdmissionId/forms-management/edit-dsm5-teen':
			t('drawerListItems.newDsm5Teen'),
		'/pre-admission/pre-admission-list/detail/:preAdmissionId/forms-management/detail-dsm5-teen':
			t('drawerListItems.dsm5TeenDetail'),

		'/user-management': t('drawerListItems.userManagement'),
		'/user-management/user-management-list': t(
			'drawerListItems.userManagementList'
		),
		'/user-management/user-management-list/create': t(
			'drawerListItems.newSystemUser'
		),
		'/user-management/user-management-list/detail': t(
			'drawerListItems.systemUserDetail'
		),
		'/user-management/user-management-list/edit': t(
			'drawerListItems.editSystemUser'
		),

		'/member-management': t('drawerListItems.memberManagement'),
		'/member-management/admission-request-list': t(
			'drawerListItems.admissionRequestSummary'
		),
		'/member-management/admission-request-list/detail/:admissionRequestId':
			`${t('drawerListItems.admissionRequestDetails')} (${
				admissionRequestDetails?.data.preAdmissionNo
			})` || t('drawerListItems.admissionRequestDetails'),
		'/member-management/admission-request-list/detail/:admissionRequestId/personal':
			t('drawerListItems.editAdmissionRequest'),
		'/member-management/admission-request-list/detail/:admissionRequestId/contact':
			t('drawerListItems.editAdmissionRequest'),
		'/member-management/admission-request-list/detail/:admissionRequestId/family':
			t('drawerListItems.editAdmissionRequest'),
		'/member-management/admission-request-list/detail/:admissionRequestId/work':
			t('drawerListItems.editAdmissionRequest'),
		'/member-management/admission-request-list/detail/:admissionRequestId/membership':
			t('drawerListItems.editAdmissionRequest'),
		'/member-management/admission-request-list/detail/:admissionRequestId/case':
			t('drawerListItems.editAdmissionRequest'),
		'/member-management/admission-request-list/detail/:admissionRequestId/referrer':
			t('drawerListItems.editAdmissionRequest'),
		'/member-management/admission-request-list/detail/:admissionRequestId/share':
			t('drawerListItems.editAdmissionRequest'),
		'/member-management/admission-request-list/detail/:admissionRequestId/health':
			t('drawerListItems.editAdmissionRequest'),

		'/member-management/pending-submission-list': t(
			'drawerListItems.pendingSubmissionSummary'
		),
		'/member-management/awaiting-list': t('drawerListItems.awaitingSummary'),
		'/member-management/pending-intake-list': t(
			'drawerListItems.pendingSummary'
		),

		'/member-management/member-list': t('drawerListItems.memberSummary'),
		'/member-management/member-list/detail/:admissionRequestId':
			`${t('drawerListItems.memberDetails')} (${
				admissionRequestDetails?.data.fileNo
			})` || t('drawerListItems.memberDetails'),
		'/member-management/member-list/detail/:admissionRequestId/forms-management': `${t(
			'drawerListItems.formsManagement'
		)}`,
		'/member-management/member-list/detail/:preAdmissionId/forms-management/create-enquiry-record':
			t('drawerListItems.newEnquiryRecord'),
		'/member-management/member-list/detail/:preAdmissionId/forms-management/edit-enquiry-record':
			t('drawerListItems.newEnquiryRecord'),
		'/member-management/member-list/detail/:preAdmissionId/forms-management/detail-enquiry-record':
			t('drawerListItems.enquiryRecordDetails'),
		'/member-management/member-list/detail/:preAdmissionId/forms-management/create-face-sheet':
			t('drawerListItems.newFaceSheet'),
		'/member-management/member-list/detail/:preAdmissionId/forms-management/edit-face-sheet':
			t('drawerListItems.newFaceSheet'),
		'/member-management/member-list/detail/:preAdmissionId/forms-management/detail-face-sheet':
			t('drawerListItems.faceSheetDetails'),

		'/member-management/member-list/detail/:preAdmissionId/forms-management/create-appendix4':
			t('drawerListItems.newAppendix4'),
		'/member-management/member-list/detail/:preAdmissionId/forms-management/edit-appendix4':
			t('drawerListItems.newAppendix4'),
		'/member-management/member-list/detail/:preAdmissionId/forms-management/detail-appendix4':
			t('drawerListItems.appendix4Detail'),
		'/member-management/member-list/detail/:preAdmissionId/forms-management/create-appendix5':
			t('drawerListItems.newAppendix5'),
		'/member-management/member-list/detail/:preAdmissionId/forms-management/edit-appendix5':
			t('drawerListItems.newAppendix5'),
		'/member-management/member-list/detail/:preAdmissionId/forms-management/detail-appendix5':
			t('drawerListItems.appendix5Detail'),
		'/member-management/member-list/detail/:preAdmissionId/forms-management/create-appendix6':
			t('drawerListItems.newAppendix6'),
		'/member-management/member-list/detail/:preAdmissionId/forms-management/edit-appendix6':
			t('drawerListItems.newAppendix6'),
		'/member-management/member-list/detail/:preAdmissionId/forms-management/detail-appendix6':
			t('drawerListItems.appendix6Detail'),

		'/member-management/member-list/detail/:preAdmissionId/forms-management/create-appendix7':
			t('drawerListItems.newAppendix7'),
		'/member-management/member-list/detail/:preAdmissionId/forms-management/edit-appendix7':
			t('drawerListItems.newAppendix7'),
		'/member-management/member-list/detail/:preAdmissionId/forms-management/detail-appendix7':
			t('drawerListItems.appendix7Detail'),

		'/member-management/member-list/detail/:preAdmissionId/forms-management/create-member-application':
			t('drawerListItems.newMemberApplication'),
		'/member-management/member-list/detail/:preAdmissionId/forms-management/edit-member-application':
			t('drawerListItems.newMemberApplication'),
		'/member-management/member-list/detail/:preAdmissionId/forms-management/detail-member-application':
			t('drawerListItems.memberApplicationDetail'),

		'/member-management/member-list/detail/:preAdmissionId/forms-management/create-new-consent':
			t('drawerListItems.newNewConsent'),
		'/member-management/member-list/detail/:preAdmissionId/forms-management/edit-new-consent':
			t('drawerListItems.newNewConsent'),
		'/member-management/member-list/detail/:preAdmissionId/forms-management/detail-new-consent':
			t('drawerListItems.newConsentDetail'),

		'/member-management/member-list/detail/:preAdmissionId/forms-management/create-stop-service':
			t('drawerListItems.newStopService'),
		'/member-management/member-list/detail/:preAdmissionId/forms-management/edit-stop-service':
			t('drawerListItems.newStopService'),
		'/member-management/member-list/detail/:preAdmissionId/forms-management/detail-stop-service':
			t('drawerListItems.stopServiceDetail'),

		'/member-management/member-list/detail/:preAdmissionId/forms-management/create-well-being-scale':
			t('drawerListItems.newWellBeingScale'),
		'/member-management/member-list/detail/:preAdmissionId/forms-management/edit-well-being-scale':
			t('drawerListItems.newWellBeingScale'),
		'/member-management/member-list/detail/:preAdmissionId/forms-management/detail-well-being-scale':
			t('drawerListItems.wellBeingScaleDetail'),

		'/member-management/member-list/detail/:preAdmissionId/forms-management/create-recovery-assessment-scale':
			t('drawerListItems.newRecoveryAssessmentScale'),
		'/member-management/member-list/detail/:preAdmissionId/forms-management/edit-recovery-assessment-scale':
			t('drawerListItems.newRecoveryAssessmentScale'),
		'/member-management/member-list/detail/:preAdmissionId/forms-management/detail-recovery-assessment-scale':
			t('drawerListItems.recoveryAssessmentScaleDetail'),

		'/member-management/member-list/detail/:preAdmissionId/forms-management/create-dsm5-adult':
			t('drawerListItems.newDsm5Adult'),
		'/member-management/member-list/detail/:preAdmissionId/forms-management/edit-dsm5-adult':
			t('drawerListItems.newDsm5Adult'),
		'/member-management/member-list/detail/:preAdmissionId/forms-management/detail-dsm5-adult':
			t('drawerListItems.dsm5AdultDetail'),

		'/member-management/member-list/detail/:preAdmissionId/forms-management/create-dsm5-teen':
			t('drawerListItems.newDsm5Teen'),
		'/member-management/member-list/detail/:preAdmissionId/forms-management/edit-dsm5-teen':
			t('drawerListItems.newDsm5Teen'),
		'/member-management/member-list/detail/:preAdmissionId/forms-management/detail-dsm5-teen':
			t('drawerListItems.dsm5TeenDetail'),

		'/member-management/admission-request-list/detail/:admissionRequestId/forms-management': `${t(
			'drawerListItems.formsManagement'
		)}`,
		'/member-management/admission-request-list/detail/:preAdmissionId/forms-management/create-enquiry-record':
			t('drawerListItems.newEnquiryRecord'),
		'/member-management/admission-request-list/detail/:preAdmissionId/forms-management/edit-enquiry-record':
			t('drawerListItems.newEnquiryRecord'),
		'/member-management/admission-request-list/detail/:preAdmissionId/forms-management/detail-enquiry-record':
			t('drawerListItems.enquiryRecordDetails'),
		'/member-management/admission-request-list/detail/:preAdmissionId/forms-management/create-face-sheet':
			t('drawerListItems.newFaceSheet'),
		'/member-management/admission-request-list/detail/:preAdmissionId/forms-management/edit-face-sheet':
			t('drawerListItems.newFaceSheet'),
		'/member-management/admission-request-list/detail/:preAdmissionId/forms-management/detail-face-sheet':
			t('drawerListItems.faceSheetDetails'),

		'/member-management/admission-request-list/detail/:preAdmissionId/forms-management/create-appendix4':
			t('drawerListItems.newAppendix4'),
		'/member-management/admission-request-list/detail/:preAdmissionId/forms-management/edit-appendix4':
			t('drawerListItems.newAppendix4'),
		'/member-management/admission-request-list/detail/:preAdmissionId/forms-management/detail-appendix4':
			t('drawerListItems.appendix4Detail'),
		'/member-management/admission-request-list/detail/:preAdmissionId/forms-management/create-appendix5':
			t('drawerListItems.newAppendix5'),
		'/member-management/admission-request-list/detail/:preAdmissionId/forms-management/edit-appendix5':
			t('drawerListItems.newAppendix5'),
		'/member-management/admission-request-list/detail/:preAdmissionId/forms-management/detail-appendix5':
			t('drawerListItems.appendix5Detail'),
		'/member-management/admission-request-list/detail/:preAdmissionId/forms-management/create-appendix6':
			t('drawerListItems.newAppendix6'),
		'/member-management/admission-request-list/detail/:preAdmissionId/forms-management/edit-appendix6':
			t('drawerListItems.newAppendix6'),
		'/member-management/admission-request-list/detail/:preAdmissionId/forms-management/detail-appendix6':
			t('drawerListItems.appendix6Detail'),

		'/member-management/admission-request-list/detail/:preAdmissionId/forms-management/create-appendix7':
			t('drawerListItems.newAppendix7'),
		'/member-management/admission-request-list/detail/:preAdmissionId/forms-management/edit-appendix7':
			t('drawerListItems.newAppendix7'),
		'/member-management/admission-request-list/detail/:preAdmissionId/forms-management/detail-appendix7':
			t('drawerListItems.appendix7Detail'),

		'/member-management/admission-request-list/detail/:preAdmissionId/forms-management/create-member-application':
			t('drawerListItems.newMemberApplication'),
		'/member-management/admission-request-list/detail/:preAdmissionId/forms-management/edit-member-application':
			t('drawerListItems.newMemberApplication'),
		'/member-management/admission-request-list/detail/:preAdmissionId/forms-management/detail-member-application':
			t('drawerListItems.memberApplicationDetail'),

		'/member-management/admission-request-list/detail/:preAdmissionId/forms-management/create-new-consent':
			t('drawerListItems.newNewConsent'),
		'/member-management/admission-request-list/detail/:preAdmissionId/forms-management/edit-new-consent':
			t('drawerListItems.newNewConsent'),
		'/member-management/admission-request-list/detail/:preAdmissionId/forms-management/detail-new-consent':
			t('drawerListItems.newConsentDetail'),

		'/member-management/admission-request-list/detail/:preAdmissionId/forms-management/create-stop-service':
			t('drawerListItems.newStopService'),
		'/member-management/admission-request-list/detail/:preAdmissionId/forms-management/edit-stop-service':
			t('drawerListItems.newStopService'),
		'/member-management/admission-request-list/detail/:preAdmissionId/forms-management/detail-stop-service':
			t('drawerListItems.stopServiceDetail'),

		'/member-management/admission-request-list/detail/:preAdmissionId/forms-management/create-well-being-scale':
			t('drawerListItems.newWellBeingScale'),
		'/member-management/admission-request-list/detail/:preAdmissionId/forms-management/edit-well-being-scale':
			t('drawerListItems.newWellBeingScale'),
		'/member-management/admission-request-list/detail/:preAdmissionId/forms-management/detail-well-being-scale':
			t('drawerListItems.wellBeingScaleDetail'),

		'/member-management/admission-request-list/detail/:preAdmissionId/forms-management/create-recovery-assessment-scale':
			t('drawerListItems.newRecoveryAssessmentScale'),
		'/member-management/admission-request-list/detail/:preAdmissionId/forms-management/edit-recovery-assessment-scale':
			t('drawerListItems.newRecoveryAssessmentScale'),
		'/member-management/admission-request-list/detail/:preAdmissionId/forms-management/detail-recovery-assessment-scale':
			t('drawerListItems.recoveryAssessmentScaleDetail'),

		'/member-management/admission-request-list/detail/:preAdmissionId/forms-management/create-dsm5-adult':
			t('drawerListItems.newDsm5Adult'),
		'/member-management/admission-request-list/detail/:preAdmissionId/forms-management/edit-dsm5-adult':
			t('drawerListItems.newDsm5Adult'),
		'/member-management/admission-request-list/detail/:preAdmissionId/forms-management/detail-dsm5-adult':
			t('drawerListItems.dsm5AdultDetail'),

		'/member-management/admission-request-list/detail/:preAdmissionId/forms-management/create-dsm5-teen':
			t('drawerListItems.newDsm5Teen'),
		'/member-management/admission-request-list/detail/:preAdmissionId/forms-management/edit-dsm5-teen':
			t('drawerListItems.newDsm5Teen'),
		'/member-management/admission-request-list/detail/:preAdmissionId/forms-management/detail-dsm5-teen':
			t('drawerListItems.dsm5TeenDetail'),

		'/member-management/member-list/detail/:admissionRequestId/personal': t(
			'drawerListItems.editMember'
		),
		'/member-management/member-list/detail/:admissionRequestId/contact': t(
			'drawerListItems.editMember'
		),
		'/member-management/member-list/detail/:admissionRequestId/family': t(
			'drawerListItems.editMember'
		),
		'/member-management/member-list/detail/:admissionRequestId/work': t(
			'drawerListItems.editMember'
		),
		'/member-management/member-list/detail/:admissionRequestId/membership': t(
			'drawerListItems.editMember'
		),
		'/member-management/member-list/detail/:admissionRequestId/case': t(
			'drawerListItems.editMember'
		),
		'/member-management/member-list/detail/:admissionRequestId/referrer': t(
			'drawerListItems.editMember'
		),
		'/member-management/member-list/detail/:admissionRequestId/share': t(
			'drawerListItems.editMember'
		),
		'/member-management/member-list/detail/:admissionRequestId/health': t(
			'drawerListItems.editMember'
		),
		'/member-management/member-list/detail/:admissionRequestId/login-id': t(
			'drawerListItems.editMember'
		),
		'/member-management/member-list/detail/:admissionRequestId/create':
			newInterventionLabel,
		'/member-management/admission-request-list/detail/:admissionRequestId/create':
			newInterventionLabel,

		'/member-management/member-list/detail/:admissionRequestId/intervention/detail/:interventionId': `${t('drawerListItems.interventionDetails')}${interventionDetails?.data.fileNo ? ` (${interventionDetails.data.fileNo})` : ''}`,
		'/member-management/admission-request-list/detail/:admissionRequestId/intervention/detail/:interventionId': `${t('drawerListItems.interventionDetails')}${interventionDetails?.data.fileNo ? ` (${interventionDetails.data.fileNo})` : ''}`,

		'/member-management/member-list/detail/:admissionRequestId/intervention/detail/:interventionId/edit/:interventionPage': `${t('drawerListItems.editIntervention')}${interventionDetails?.data.fileNo ? ` (${interventionDetails.data.fileNo})` : ''}`,
		'/member-management/member-list/detail/:admissionRequestId/intervention/detail/:interventionId/edit-case-note/:interventionPage': `${t('drawerListItems.editIntervention')}${interventionDetails?.data.fileNo ? ` (${interventionDetails.data.fileNo})` : ''}`,
		'/member-management/admission-request-list/detail/:admissionRequestId/intervention/detail/:interventionId/edit/:interventionPage': `${t('drawerListItems.editIntervention')}${interventionDetails?.data.fileNo ? ` (${interventionDetails.data.fileNo})` : ''}`,
		'/member-management/admission-request-list/detail/:admissionRequestId/intervention/detail/:interventionId/edit-case-note/:interventionPage': `${t('drawerListItems.editIntervention')}${interventionDetails?.data.fileNo ? ` (${interventionDetails.data.fileNo})` : ''}`,

		'/case-management': t('drawerListItems.caseManagement'),
		'/case-management/my-case': t('drawerListItems.myCaseSummary'),
		'/case-management/my-case/create': newInterventionLabel,
		'/case-management/centre-case': t('drawerListItems.centreCaseSummary'),
		'/case-management/centre-case/create': newInterventionLabel,
		'/case-management/my-program': t('drawerListItems.myProgramMemberSummary'),
		'/case-management/my-program/create': newInterventionLabel,
		'/case-management/centre-program': t(
			'drawerListItems.centreProgramMemberSummary'
		),

		'/case-management/centre-program/create': newInterventionLabel,
		'/case-management/intervention': t('drawerListItems.myInterventionSummary'),
		'/case-management/intervention/create': newInterventionLabel,
		'/case-management/intervention/detail/:interventionId': `${t('drawerListItems.interventionDetails')}${interventionDetails?.data.fileNo ? ` (${interventionDetails.data.fileNo})` : ''}`,
		'/case-management/intervention/detail/:interventionId/edit/:interventionPage': `${t('drawerListItems.editIntervention')}${interventionDetails?.data.fileNo ? ` (${interventionDetails.data.fileNo})` : ''}`,
		'/case-management/intervention/detail/:interventionId/edit-case-note/:interventionPage': `${t('drawerListItems.editIntervention')}${interventionDetails?.data.fileNo ? ` (${interventionDetails.data.fileNo})` : ''}`,
		'/case-management/centre-intervention': t(
			'drawerListItems.centreInterventionSummary'
		),

		'/case-management/centre-intervention/detail/:interventionId': `${t('drawerListItems.interventionDetails')}${interventionDetails?.data.fileNo ? ` (${interventionDetails.data.fileNo})` : ''}`,
		'/case-management/centre-intervention/detail/:interventionId/edit/:interventionPage': `${t('drawerListItems.editIntervention')}${interventionDetails?.data.fileNo ? ` (${interventionDetails.data.fileNo})` : ''}`,
		'/case-management/centre-intervention/detail/:interventionId/edit-case-note/:interventionPage': `${t('drawerListItems.editIntervention')}${interventionDetails?.data.fileNo ? ` (${interventionDetails.data.fileNo})` : ''}`,
		'/case-management/centre-intervention/create': newInterventionLabel,
		'/family-management': t('drawerListItems.familyMemberManagement'),
		'/family-management/family-management-list': t(
			'drawerListItems.familyMemberSummary'
		),
		'/family-management/family-management-list/create': t(
			'drawerListItems.newFamilyMember'
		),
		'/family-management/family-management-list/detail/:familyId': t(
			`${t('drawerListItems.familyMemberDetails')} (${
				familyDetail?.data.familyMemberFileNo
			})` || t('drawerListItems.familyMemberDetails')
		),
		'/family-management/family-management-list/detail/:familyId/edit-service-details':
			t('drawerListItems.editFamilyMember'),
		'/family-management/family-management-list/detail/:familyId/edit-personal':
			t('drawerListItems.editFamilyMember'),
		'/family-management/family-management-list/detail/:familyId/edit-education':
			t('drawerListItems.editFamilyMember'),
		'/family-management/family-management-list/detail/:familyId/edit-remarks':
			t('drawerListItems.editFamilyMember'),
		'/family-management/family-management-list/detail/:familyId/edit-login-id':
			t('drawerListItems.editFamilyMember'),
		'/family-management/family-management-list/detail/:familyId/edit-program-app-settings':
			t('drawerListItems.editFamilyMember'),
		'/family-management/family-management-list/detail/:familyId/forms-management':
			t('drawerListItems.formsManagement'),
		'/family-management/family-management-list/detail/:familyId/forms-management/detail':
			t('drawerListItems.familyMemberApplicationFormDetails'),

		'/report-summary': t('drawerListItems.reportSummary'),

		'/notifications': t('notification.title'),
		'/notifications/detail/:notificationId': t('notification.details'),

		'/field-management': t('drawerListItems.FieldOptionManagement'),
		'/field-management/field-management-list': t(
			'drawerListItems.FieldOptionSummary'
		),
		'/field-management/field-management-list/unit/:field': t('common.unit'),
		'/field-management/field-management-list/unit/:field/status': t(
			'drawerListItems.unitStatus'
		),
		'/field-management/field-management-list/unit/:field/order': t(
			'drawerListItems.unitStatus'
		),

		'/field-management/field-management-list/region/:field': t('member.region'),
		'/field-management/field-management-list/region/:field/status': t(
			'drawerListItems.regionStatus'
		),
		'/field-management/field-management-list/region/:field/order': t(
			'drawerListItems.regionStatus'
		),

		'/field-management/field-management-list/region/:field/district/:districtId': `${t(
			'member.district'
		)} (${districtDetails?.data.nameEn})`,
		'/field-management/field-management-list/region/:field/district/:districtId/status': `${t(
			'field.manageStatus'
		)} (${districtDetails?.data.nameEn})`,
		'/field-management/field-management-list/region/:field/district/:districtId/order': `${t(
			'field.manageStatus'
		)} (${districtDetails?.data.nameEn})`,

		'/field-management/field-management-list/region/:field/district/:districtId/sub-district/:subDistrictId': `${t(
			'member.subdistrict'
		)} (${subDistrictDetails?.data.nameEn})`,
		'/field-management/field-management-list/region/:field/district/:districtId/sub-district/:subDistrictId/status': `${t(
			'field.manageStatus'
		)} (${subDistrictDetails?.data.nameEn})`,
		'/field-management/field-management-list/region/:field/district/:districtId/sub-district/:subDistrictId/order': `${t(
			'field.manageStatus'
		)} (${subDistrictDetails?.data.nameEn})`,

		'/field-management/field-management-list/region/:field/district/:districtId/sub-district/:subDistrictId/ecv/:ecvId': `${t(
			'field.ecv'
		)} (${evcDistrictDetails?.data.nameEn})`,
		'/field-management/field-management-list/region/:field/district/:districtId/sub-district/:subDistrictId/ecv/:ecvId/status': `${t(
			'field.manageStatus'
		)} (${evcDistrictDetails?.data.nameEn})`,
		'/field-management/field-management-list/region/:field/district/:districtId/sub-district/:subDistrictId/ecv/:ecvId/order': `${t(
			'field.manageStatus'
		)} (${evcDistrictDetails?.data.nameEn})`,

		'/field-management/field-management-list/region/:field/district/:districtId/sub-district/:subDistrictId/ecv/:ecvId/build/:buildId': `${t(
			'field.bb'
		)} (${buildDetails?.data.nameEn})`,
		'/field-management/field-management-list/region/:field/district/:districtId/sub-district/:subDistrictId/ecv/:ecvId/build/:buildId/status': `${t(
			'field.manageStatus'
		)} (${buildDetails?.data.nameEn})`,
		'/field-management/field-management-list/region/:field/district/:districtId/sub-district/:subDistrictId/ecv/:ecvId/build/:buildId/order': `${t(
			'field.manageStatus'
		)} (${buildDetails?.data.nameEn})`,

		'/field-management/field-management-list/field/:fieldId/:fieldType': `${dictDetails?.data.labelDisplay}`,
		'/field-management/field-management-list/field/:fieldId/:fieldType/status': `${t(
			'field.manageStatus'
		)} (${dictDetails?.data.labelDisplay})`,
		'/field-management/field-management-list/field/:fieldId/:fieldType/order': `${t(
			'field.manageStatus'
		)} (${dictDetails?.data.labelDisplay})`,

		'/field-management/field-management-list/field/:fieldId/:fieldType/agency/:agencyId/:agencyType': `${agencyDetails?.data.labelDisplay}`,
		'/field-management/field-management-list/field/:fieldId/:fieldType/agency/:agencyId/:agencyType/status': `${t(
			'field.manageStatus'
		)} (${agencyDetails?.data.labelDisplay})`,
		'/field-management/field-management-list/field/:fieldId/:fieldType/agency/:agencyId/:agencyType/order': `${t(
			'field.manageStatus'
		)} (${agencyDetails?.data.labelDisplay})`,

		'/field-management/field-management-list/field/:fieldId/:fieldType/agency/:agencyId/:agencyType/referral/:referralId/:referralType': `${referralDetails?.data.labelDisplay}`,
		'/field-management/field-management-list/field/:fieldId/:fieldType/agency/:agencyId/:agencyType/referral/:referralId/:referralType/status': `${t(
			'field.manageStatus'
		)} (${referralDetails?.data.labelDisplay})`,
		'/field-management/field-management-list/field/:fieldId/:fieldType/agency/:agencyId/:agencyType/referral/:referralId/:referralType/order': `${t(
			'field.manageStatus'
		)} (${referralDetails?.data.labelDisplay})`,
	};

	for (let index = pathnames.length - 1; index >= 0; index--) {
		const to = `/${pathnames.slice(0, index + 1).join('/')}`;
		const pathMatch = useRouteMatch(to, Object.keys(breadcrumbNameMap));
		if (pathMatch) {
			lastPath = pathMatch?.pattern?.path;
			break;
		}
	}

	return (
		<Breadcrumbs separator='›' sx={{ marginBottom: 3 }}>
			{/* <LinkRouter underline='hover' color='inherit' to='/'>
				{t('drawerListItems.home')}
			</LinkRouter> */}

			{pathnames.map((_, index) => {
				const to = `/${pathnames.slice(0, index + 1).join('/')}`;
				const pathMatch = useRouteMatch(to, Object.keys(breadcrumbNameMap));
				const last = pathMatch?.pattern?.path == lastPath;
				const currentPath = pathMatch?.pattern?.path;

				if (!pathMatch || !currentPath) return null;

				const disabledPaths = [
					'/pre-admission',
					'/user-management',
					'/member-management',
					'/case-management',
					'/family-management',
					'/field-management',
				];
				const isDisabled = disabledPaths?.includes(currentPath);

				return (
					pathMatch &&
					(last || isDisabled ? (
						<Typography
							color={last ? 'text.primary' : 'rgba(0, 0, 0, 0.6)'}
							key={to}
						>
							{breadcrumbNameMap[pathMatch?.pattern?.path]}
						</Typography>
					) : (
						<LinkRouter underline='hover' color='inherit' to={to} key={to}>
							{breadcrumbNameMap[pathMatch?.pattern?.path]}
						</LinkRouter>
					))
				);
			})}
		</Breadcrumbs>
	);
}
